import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Box, Typography, Button, Paper, Divider, 
  CircularProgress, Alert, AlertTitle 
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import HomeIcon from '@mui/icons-material/Home';
import PaymentIcon from '@mui/icons-material/Payment';
import axios from 'axios';
import primaryBackground from "../assets/Backgrounds/primaryBg.svg";

const PaymentError = () => {
  const [errorData, setErrorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchErrorDetails = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const transactionToken = params.get('ID');
        const errorMessage = params.get('message');
        
        if (errorMessage) {
          // Direct error case (no token needed)
          setErrorData({
            status: 'error',
            message: errorMessage
          });
          setLoading(false);
          return;
        }

        if (!transactionToken) {
          throw new Error('Transaction token not found in URL');
        }

        const formData = new FormData();
        formData.append("transToken", transactionToken);

        // In production, you would call your backend API
        const response = await axios.post(
          "https://mindsinaction.com.na/api/dpo/cancel_token.php",

          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            withCredentials: true,
          }
        );

        const result = response.data;

        if (!result.success) {
          throw new Error(result.message || 'Failed to verify payment cancellation');
        }

        setErrorData(result.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchErrorDetails();
  }, [location.search]);

  const handleReturnHome = () => {
    navigate('/');
  };

  const handleRetryPayment = () => {
    // You might want to redirect to the payment page or restore the cart
    navigate('/get-started');
  };

  if (loading) {
    return (
      <Box
        width={'100%'}
        height={'100%'}
        display={'flex'}
        flexDirection={'column'}
        alignItems={'center'}
        justifyContent={'center'}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        width={'100%'}
        height={'100%'}
        display={'flex'}
        flexDirection={'column'}
        alignItems={'center'}
        justifyContent={'center'}
        sx={{
                  backgroundImage: `url(${primaryBackground})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }}
      >
        <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
          <Alert severity="error">
            <AlertTitle>Payment Error</AlertTitle>
            {error}
          </Alert>
          <Button 
            variant="contained" 
            startIcon={<HomeIcon />}
            onClick={handleReturnHome}
            sx={{ mt: 2 }}
          >
            Return Home
          </Button>
        </Box>
      </Box>
      
    );
  }

  if (!errorData) {
    return null;
  }

  return (
    <Box
      width={'100%'}
      height={'100%'}
      display={'flex'}
      flexDirection={'column'}
      alignItems={'center'}
      justifyContent={'center'}
      sx={{
          backgroundImage: `url(${primaryBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
    >
      <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <CancelIcon sx={{ fontSize: 60, color: 'error.main' }} />
            <Typography variant="h4" component="h1" gutterBottom>
              Payment {errorData.status === 'cancelled' ? 'Cancelled' : 'Error'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {errorData.message || 'Your payment was not completed.'}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {errorData.transactionToken && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" color='green' gutterBottom>
                All of your details that you provided has been deleted successfully.
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={handleReturnHome}
            >
              Return Home
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<PaymentIcon />}
              onClick={handleRetryPayment}
            >
              Try Payment Again
            </Button>
          </Box>
        </Paper>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Need help? Contact our support team at support@mindsinaction.com.na
          </Typography>
        </Box>
      </Box>
    </Box>
    
  );
};

export default PaymentError;